const enabledNames = (items, nameKey = 'name') => (items ?? [])
    .filter((item) => item?.enabled !== false)
    .map((item) => item?.[nameKey])
    .filter(Boolean);

export const isShoptetAddonIntegration = (integration) => integration?.name === 'ShoptetAddon';
export const isAddonIntegration = (integration) => ['ShoptetAddon', 'UpgatesAddon'].includes(integration?.name);

// Sklik and Zbozi do not expose selectable account/source summaries in the UI.
export function hasSelectableIntegrationSources(integration) {
    return !['Sklik', 'Zbozi', 'UpgatesAddon'].includes(integration?.name);
}

/** Resolve the selected BSShop centre IDs to the same labels shown by its settings form. */
export function getBsShopSelectedCentreNames(integration, customerType) {
    if (integration?.name !== 'BsShop') return [];

    const selectedIds = customerType === 'b2c' ? integration.b2cCentres : integration.b2bCentres;
    const centres = integration.knownCentres ?? {};
    const knownCentres = Array.isArray(centres)
        ? Object.fromEntries(centres.map((name, index) => [String(index), name]))
        : centres;

    return [...new Set((selectedIds ?? []).map((id) => {
        const key = String(id);
        const name = knownCentres[key];
        if (typeof name !== 'string' || !name.trim()) return `ID ${key}`;

        const displayName = name.trim().replace(/^\[[^\]]+\]\s*/, '').trim();
        return displayName || `ID ${key}`;
    }))];
}

export function canManageIntegrationAction(integration, action) {
    if (!integration || !['pause', 'resume', 'delete'].includes(action)) return false;
    if (!isShoptetAddonIntegration(integration)) return true;
    return action === 'delete' && integration.status === 'inactive';
}

export function getIntegrationLastUpdate(integration) {
    return [integration.lastRegularUpdate, integration.lastHistoricalUpdate]
        .filter((value) => value && !Number.isNaN(new Date(value).getTime()))
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? null;
}

export function getIntegrationSourceNames(integration) {
    if (isShoptetAddonIntegration(integration)) return integration.eshopUrl ? [integration.eshopUrl] : [];
    const xmlHostNames = integration?.name === 'Shoptet'
        ? (integration.hostNameList ?? []).map((hostName) => hostName?.trim()).filter(Boolean)
        : [];
    const candidates = [
        ...enabledNames(integration.googleIntegrationCustomerClients),
        ...enabledNames(integration.metaIntegrationAdAccounts),
        ...enabledNames(integration.microsoftIntegrationCustomers),
        ...enabledNames(integration.pinterestIntegrationAdAccounts),
        ...enabledNames(integration.tikTokIntegrationAdAccounts),
        ...enabledNames(integration.criteoIntegrationAdAccounts),
        ...(integration.providerResources ?? []).map((resource) => resource?.displayName).filter(Boolean),
        ...(integration.hostNameAndApiKeyList ?? []).map((resource) => resource?.hostName).filter(Boolean),
        ...(integration.hostNameAndXmlLinkList ?? []).map((resource) => resource?.hostName).filter(Boolean),
        ...(integration.hostNameApiIdApiKeyList ?? []).map((resource) => resource?.hostName).filter(Boolean),
        ...(integration.hostNameAndTldAndApiCurrencyCodeApiKeyData ?? []).map((resource) => resource?.hostName).filter(Boolean),
        ...xmlHostNames,
    ];

    if (candidates.length === 0 && integration.domain) {
        candidates.push(integration.domain);
    }

    return [...new Set(candidates)];
}

export function getIntegrationState(integration) {
    if (isShoptetAddonIntegration(integration)) {
        return ['active', 'paused', 'inactive'].includes(integration.status) ? integration.status : 'inactive';
    }
    if (integration.paused) return 'paused';
    if (!integration.authorizationSuccessful) return 'inactive';
    return 'active';
}

export function formatIntegrationDateTime(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const pad = (number) => String(number).padStart(2, '0');
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}
